/**
 * FilterChips.tsx - Filter Display Chips Component
 *
 * Displays active filters as badge-style chips outside the message bubble.
 * Shows filter type labels and supports RTL layouts.
 */

import React from 'react';
import { HStack, Text, Box } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { FilterOption } from '@/types/filter.types';
import { formatDateToDDMMYYYY } from '@/utilities/formatDate';
import { useFilterContext } from '@/contexts/FilterContext';
import { getTranslationKey } from '@/utilities/getTranslationKey';
import { extractItemCode, extractRootCode, findBestMatchingCode, normalizeDeweyCode, HIDE_UNRESOLVED_CATEGORIES, isLabelResolved } from '@/utilities/deweyCodeParser';
interface FilterChipsProps {
    filters: {
        dateRange?: {
            from?: string;
            to?: string;
        };
        authors?: string[];
        persons?: string[];
        organizations?: string[];
        locations?: string[];
        categoryCodes?: string[];
        tags?: string[];
    };
    suggestions?: {
        authors?: FilterOption[];
        persons?: FilterOption[];
        organizations?: FilterOption[];
        locations?: FilterOption[];
        categoryCodes?: FilterOption[];
    } | null;
    messageContent?: string;
    onEditClick?: () => void;
}

export const FilterChips: React.FC<FilterChipsProps> = ({ filters, suggestions }) => {
    const { t, i18n } = useTranslation();
    const isRTL = i18n.dir() === 'rtl';
    const { findCategoryLabel } = useFilterContext();

    const getDisplayLabel = (value: string, options?: FilterOption[]): string => {
        if (!options) return value;
        const option = options.find((opt) => opt.value === value);
        return option?.displayLabel || value;
    };

    const getDateRangeLabel = (): string | null => {
        if (!(filters.dateRange?.from || filters.dateRange?.to)) {
            return null;
        }
    
        const hasFrom = Boolean(filters.dateRange?.from);
        const hasTo = Boolean(filters.dateRange?.to);
    
        if (hasFrom && hasTo) {
            const from = formatDateToDDMMYYYY(filters.dateRange!.from as string);
            const to = formatDateToDDMMYYYY(filters.dateRange!.to as string);
            return `${from} – ${to}`;
        }
    
        if (hasFrom) {
            const from = formatDateToDDMMYYYY(filters.dateRange!.from as string);
            return `${t('filter_after') || 'After'} ${from}`;
        }
    
        const to = formatDateToDDMMYYYY(filters.dateRange!.to as string);
        return `${t('filter_before') || 'Before'} ${to}`;
    };

    // Use global category label mapping from context and translate it
    // Handle complex codes like:
    // - "900.956.704" -> Extract "956.704" and look up in tree
    // - "956.704.01" -> Normalize to "956.70401" and look up
    // Returns null if HIDE_UNRESOLVED_CATEGORIES is true and no label was found
    const getCategoryLabel = (fullCode: string): string | null => {
        let rawLabel: string | null = null;
        
        // Try direct lookup first
        let label = findCategoryLabel(fullCode);
        if (label !== fullCode) {
            rawLabel = label;
        }
        
        // Try with extracted item code (for "900.956.704" → "956.704")
        if (!rawLabel) {
            const itemCode = extractItemCode(fullCode);
            label = findCategoryLabel(itemCode);
            if (label !== itemCode) {
                rawLabel = label;
            }
        }
        
        // Try normalized variations (for "956.704.01" → "956.70401")
        if (!rawLabel) {
            const variations = normalizeDeweyCode(fullCode);
            for (const variation of variations) {
                if (variation !== fullCode) {
                    label = findCategoryLabel(variation);
                    if (label !== variation) {
                        rawLabel = label;
                        break;
                    }
                }
            }
        }
        
        // Try normalized variations of item code
        if (!rawLabel) {
            const itemCode = extractItemCode(fullCode);
            const itemVariations = normalizeDeweyCode(itemCode);
            for (const variation of itemVariations) {
                if (variation !== itemCode) {
                    label = findCategoryLabel(variation);
                    if (label !== variation) {
                        rawLabel = label;
                        break;
                    }
                }
            }
        }
        
        // Fallback: use findBestMatchingCode for prefix search
        if (!rawLabel) {
            const itemCode = extractItemCode(fullCode);
            const rootCode = extractRootCode(fullCode);
            const bestCode = findBestMatchingCode(itemCode, rootCode, findCategoryLabel);
            rawLabel = findCategoryLabel(bestCode);
        }
        
        // Check if we resolved a label or just got the code back
        if (!rawLabel || !isLabelResolved(fullCode, rawLabel)) {
            // No label found - return null if feature flag is enabled, otherwise return the code
            if (HIDE_UNRESOLVED_CATEGORIES) {
                return null;
            }
            return fullCode;
        }
        
        // Try to translate the label
        const translationKey = getTranslationKey(rawLabel);
        const translated = t(translationKey, { ns: 'topics' });
        return translated !== translationKey ? translated : rawLabel;
    };

    const dateRangeLabel = getDateRangeLabel();
    const hasFilters =
        dateRangeLabel ||
        (filters.authors && filters.authors.length > 0) ||
        (filters.persons && filters.persons.length > 0) ||
        (filters.organizations && filters.organizations.length > 0) ||
        (filters.locations && filters.locations.length > 0) ||
        (filters.categoryCodes && filters.categoryCodes.length > 0) ||
        (filters.tags && filters.tags.length > 0);

    if (!hasFilters) {
        return null;
    }

    // Badge-style chip
    const badgeStyle = {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
        paddingX: '8px',
        paddingY: '4px',
        borderRadius: '4px',
        background: 'var(--cair-accent-subtle)',
        fontSize: '12px',
        lineHeight: '1',
    };

    const labelStyle = {
        fontWeight: '600',
        color: '#6B7280',
    };

    const valueStyle = {
        fontWeight: '500',
        color: '#374151',
    };

    // Helper to render values without extra spacing
    const renderValues = (values: string[], options?: FilterOption[]) => {
        return values.map((val) => getDisplayLabel(val, options)).join(', ');
    };

    return (
        <HStack 
            gap={1} 
            flexWrap="wrap" 
            alignItems="center"
            justifyContent={isRTL ? 'flex-start' : 'flex-end'}
            dir={isRTL ? 'rtl' : 'ltr'}
        >
            {/* Date Range */}
            {dateRangeLabel && (
                <Box {...badgeStyle}>
                    <Text {...labelStyle}>{t('filter_publication_date') || 'Date'}:</Text>
                    <Text {...valueStyle} dir="auto">{dateRangeLabel}</Text>
                </Box>
            )}

            {/* Authors */}
            {filters.authors && filters.authors.length > 0 && (
                <Box {...badgeStyle}>
                    <Text {...labelStyle}>{t('filter_author') || 'Author'}:</Text>
                    <Text {...valueStyle} dir="auto">
                        {renderValues(filters.authors, suggestions?.authors)}
                    </Text>
                </Box>
            )}

            {/* Persons */}
            {filters.persons && filters.persons.length > 0 && (
                <Box {...badgeStyle}>
                    <Text {...labelStyle}>{t('filter_person') || 'Person'}:</Text>
                    <Text {...valueStyle} dir="auto">
                        {renderValues(filters.persons, suggestions?.persons)}
                    </Text>
                </Box>
            )}

            {/* Organizations */}
            {filters.organizations && filters.organizations.length > 0 && (
                <Box {...badgeStyle}>
                    <Text {...labelStyle}>{t('filter_organization') || 'Organization'}:</Text>
                    <Text {...valueStyle} dir="auto">
                        {renderValues(filters.organizations, suggestions?.organizations)}
                    </Text>
                </Box>
            )}

            {/* Locations */}
            {filters.locations && filters.locations.length > 0 && (
                <Box {...badgeStyle}>
                    <Text {...labelStyle}>{t('filter_location') || 'Location'}:</Text>
                    <Text {...valueStyle} dir="auto">
                        {renderValues(filters.locations, suggestions?.locations)}
                    </Text>
                </Box>
            )}

            {/* Categories */}
            {filters.categoryCodes && filters.categoryCodes.length > 0 && (() => {
                const resolvedLabels = filters.categoryCodes
                    .map(getCategoryLabel)
                    .filter((label): label is string => label !== null);
                
                if (resolvedLabels.length === 0) return null;
                
                return (
                    <Box {...badgeStyle}>
                        <Text {...labelStyle}>{t('filter_topics') || 'Topics'}:</Text>
                        <Text {...valueStyle} dir="auto">
                            {resolvedLabels.join(', ')}
                        </Text>
                    </Box>
                );
            })()}

            {/* Tags */}
            {filters.tags && filters.tags.length > 0 && (
                <Box {...badgeStyle}>
                    <Text {...labelStyle}>{t('tags') || 'Tags'}:</Text>
                    <Text {...valueStyle} dir="auto">
                        {filters.tags.join(', ')}
                    </Text>
                </Box>
            )}
        </HStack>
    );
};